/**
 * Notes & Coaching — Core-100 owner direction 11 (CORE-H).
 *
 * This used to be one free-text column, `technician_profiles.notes`. The only UPDATE policy on that
 * table is the person's own, so a manager's "Save" matched no row and still said "saved", while the
 * technician could rewrite their own coaching. It is now a record (`coaching_notes`):
 *
 * - written by a manager or owner of a shop the person works in, as themselves, with the shop (and
 *   optionally the ticket) snapshotted so a note keeps its meaning after either is gone;
 * - read by the person it is about and by managers of the shop it was written in — the database
 *   decides that (RLS), this component only renders what it is given;
 * - append-only: a correction is a new note that supersedes the earlier one, and both stay visible.
 *
 * Whatever was saved in the old column is still shown, labelled as an undated earlier note.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare, Lock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface PassportCoachingNotesProps {
  technicianProfileId: string;
  /** What the retired `technician_profiles.notes` column holds — shown, never written. */
  initialNotes: string | null;
  isManagerView: boolean;
}

export interface CoachingNoteRow {
  id: string;
  body: string;
  created_at: string;
  shop_name_snapshot: string | null;
  author_name_snapshot: string | null;
  ticket_label: string | null;
  ticket_deleted_at: string | null;
  supersedes_id: string | null;
}

export const COACHING_NOTE_MAX = 4000;

/** Newest first; a note that a later note supersedes is marked, never hidden. */
export function orderCoachingNotes(rows: readonly CoachingNoteRow[]): Array<CoachingNoteRow & { superseded: boolean }> {
  const supersededIds = new Set(rows.map((r) => r.supersedes_id).filter((x): x is string => !!x));
  return [...rows]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .map((r) => ({ ...r, superseded: supersededIds.has(r.id) }));
}

export function PassportCoachingNotes({ technicianProfileId, initialNotes, isManagerView }: PassportCoachingNotesProps) {
  const { activeShop } = useAuth();
  const [notes, setNotes] = useState<CoachingNoteRow[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('coaching_notes' as never)
      .select('id, body, created_at, shop_name_snapshot, author_name_snapshot, ticket_label, ticket_deleted_at, supersedes_id')
      .eq('technician_profile_id', technicianProfileId)
      .order('created_at', { ascending: false });
    if (error) { setLoadFailed(true); setNotes([]); return; }
    setLoadFailed(false);
    setNotes(((data ?? []) as unknown) as CoachingNoteRow[]);
  }, [technicianProfileId]);

  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    const body = draft.trim();
    if (!body || !activeShop?.id) return;
    setSaving(true);
    const { error } = await supabase
      .from('coaching_notes' as never)
      .insert({ technician_profile_id: technicianProfileId, shop_id: activeShop.id, body } as never);
    setSaving(false);
    if (error) {
      toast.error('The note was not saved', { description: error.message.includes('COACHING_SUBJECT_NOT_IN_SHOP')
        ? 'Coaching is written by a shop this person works in.' : 'Only a manager or owner of this shop can write coaching.' });
      return;
    }
    setDraft('');
    toast.success('Coaching note added');
    void load();
  };

  const ordered = orderCoachingNotes(notes ?? []);
  const legacy = (initialNotes ?? '').trim();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-muted-foreground" />
            Notes & Coaching
          </CardTitle>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" />
            Written by managers · a permanent record
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isManagerView && (
          <div className="space-y-2" data-testid="coaching-compose">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, COACHING_NOTE_MAX))}
              placeholder="Add coaching: what went well, what to practise next, training goals…"
              className="min-h-[100px] resize-y"
              aria-label="New coaching note"
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                The technician can read this. Notes cannot be edited — add a new note to correct one.
              </p>
              <Button onClick={() => void save()} disabled={!draft.trim() || saving} size="sm">
                {saving ? 'Saving…' : 'Add note'}
              </Button>
            </div>
          </div>
        )}

        {loadFailed && (
          <p role="alert" className="text-sm text-destructive">Coaching notes could not be loaded.</p>
        )}
        {notes !== null && !loadFailed && ordered.length === 0 && !legacy && (
          <p className="text-sm text-muted-foreground" data-testid="coaching-empty">No coaching notes yet.</p>
        )}
        <ul className="space-y-3" data-testid="coaching-list">
          {ordered.map((n) => (
            <li key={n.id} className={`rounded-md border p-3 ${n.superseded ? 'opacity-70' : ''}`} data-testid="coaching-note">
              <p className="text-sm whitespace-pre-wrap">{n.body}</p>
              <p className="mt-2 text-xs text-muted-foreground">
                {new Date(n.created_at).toLocaleDateString()}
                {n.author_name_snapshot ? ` · ${n.author_name_snapshot}` : ''}
                {n.shop_name_snapshot ? ` · ${n.shop_name_snapshot}` : ''}
                {n.ticket_label ? ` · ${n.ticket_label}${n.ticket_deleted_at ? ' (ticket since deleted)' : ''}` : ''}
                {n.superseded ? ' · corrected by a later note' : ''}
              </p>
            </li>
          ))}
          {legacy && (
            <li className="rounded-md border border-dashed p-3" data-testid="coaching-legacy">
              <p className="text-sm whitespace-pre-wrap">{legacy}</p>
              <p className="mt-2 text-xs text-muted-foreground">Earlier note (undated, author not recorded)</p>
            </li>
          )}
        </ul>
      </CardContent>
    </Card>
  );
}
